'use client';

import { type FC, useEffect, useRef } from 'react';

const CYCLE_DURATION = 3600;
const FRAME_INTERVAL = 1000 / 30;

const PATROL_POINTS = [
  [0.2, 0.25],
  [0.48, 0.15],
  [0.8, 0.24],
  [0.86, 0.52],
  [0.72, 0.8],
  [0.4, 0.84],
  [0.14, 0.66],
  [0.13, 0.38],
] as const;

const LAUNCH_CURVE = [[0.5, 0.5], [0.39, 0.39], [0.13, 0.296], PATROL_POINTS[0]] as const;

interface Point {
  x: number;
  y: number;
}

interface ArcPoint extends Point {
  distance: number;
}

interface Dot {
  x: number;
  y: number;
  nx: number;
  ny: number;
  radius: number;
  centerDistance: number;
  noise: number;
  sin3: number;
  cos3: number;
  sin7: number;
  cos7: number;
}

interface WaveState {
  sourceX: number;
  sourceY: number;
  radius: number;
  birth: number;
  fade: number;
  sin3: number;
  cos3: number;
  sin7: number;
  cos7: number;
}

interface FrameState {
  scaleX: number;
  scaleY: number;
  waves: WaveState[];
}

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const ease = (value: number) => value * value * (3 - 2 * value);
const smoothRange = (start: number, end: number, value: number) => ease(clamp((value - start) / (end - start)));
const hash = (x: number, y: number) => {
  const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return value - Math.floor(value);
};

const catmullRom = (a: number, b: number, c: number, d: number, t: number) => {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
};

const samplePatrolRaw = (progress: number): Point => {
  const count = PATROL_POINTS.length;
  const segment = Math.floor(progress) % count;
  const t = progress - Math.floor(progress);
  const a = PATROL_POINTS[(segment - 1 + count) % count];
  const b = PATROL_POINTS[segment];
  const c = PATROL_POINTS[(segment + 1) % count];
  const d = PATROL_POINTS[(segment + 2) % count];
  return {
    x: catmullRom(a[0], b[0], c[0], d[0], t),
    y: catmullRom(a[1], b[1], c[1], d[1], t),
  };
};

const sampleLaunchRaw = (progress: number): Point => {
  const inverse = 1 - progress;
  const a = inverse * inverse * inverse;
  const b = 3 * inverse * inverse * progress;
  const c = 3 * inverse * progress * progress;
  const d = progress * progress * progress;

  return {
    x: LAUNCH_CURVE[0][0] * a + LAUNCH_CURVE[1][0] * b + LAUNCH_CURVE[2][0] * c + LAUNCH_CURVE[3][0] * d,
    y: LAUNCH_CURVE[0][1] * a + LAUNCH_CURVE[1][1] * b + LAUNCH_CURVE[2][1] * c + LAUNCH_CURVE[3][1] * d,
  };
};

const buildArcTable = (pointAt: (progress: number) => Point, steps: number): ArcPoint[] => {
  const first = pointAt(0);
  const table: ArcPoint[] = [{ distance: 0, ...first }];
  let previous = first;
  let distance = 0;

  for (let index = 1; index <= steps; index += 1) {
    const point = pointAt(index / steps);
    distance += Math.hypot(point.x - previous.x, point.y - previous.y);
    table.push({ distance, ...point });
    previous = point;
  }

  return table;
};

const sampleArc = (table: ArcPoint[], distance: number): Point => {
  let low = 0;
  let high = table.length - 1;

  while (low + 1 < high) {
    const middle = (low + high) >> 1;
    if (table[middle].distance < distance) low = middle;
    else high = middle;
  }

  const start = table[low];
  const end = table[high];
  const span = Math.max(0.000001, end.distance - start.distance);
  const mix = clamp((distance - start.distance) / span);

  return {
    x: start.x + (end.x - start.x) * mix,
    y: start.y + (end.y - start.y) * mix,
  };
};

const LAUNCH_ARC = buildArcTable(sampleLaunchRaw, 160);
const PATROL_ARC = buildArcTable((progress) => samplePatrolRaw(progress * PATROL_POINTS.length), 720);
const LAUNCH_LENGTH = LAUNCH_ARC[LAUNCH_ARC.length - 1].distance;
const PATROL_LENGTH = PATROL_ARC[PATROL_ARC.length - 1].distance;

const getParticleAlpha = (dot: Dot, frame: FrameState) => {
  let alpha = 0;

  for (let wave = 0; wave < 3; wave += 1) {
    const state = frame.waves[wave];
    const distanceFromSource = Math.hypot(
      (dot.nx - state.sourceX) * frame.scaleX,
      (dot.ny - state.sourceY) * frame.scaleY,
    );
    const irregularity =
      (dot.sin3 * state.cos3 + dot.cos3 * state.sin3) * 0.052 +
      (dot.sin7 * state.cos7 + dot.cos7 * state.sin7) * 0.022 +
      (dot.noise - 0.5) * 0.022;
    const distance = Math.abs(distanceFromSource - state.radius - irregularity);
    const band = 1 - smoothRange(0.025, 0.16, distance);
    const porous = 0.56 + dot.noise * 0.34;
    alpha += band * state.birth * state.fade * porous * 0.48;
  }

  return Math.min(0.54, alpha);
};

export const TaskStatusAnimation: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    const flowCanvas = document.createElement('canvas');
    const flowContext = flowCanvas.getContext('2d');
    if (!flowContext) return;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let width = 1;
    let height = 1;
    let dpr = 1;
    let dots: Dot[] = [];
    let animationFrame = 0;
    let running = false;
    let isVisible = true;
    let lastFrameAt = 0;
    let startTime = performance.now();

    const rebuild = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      flowCanvas.width = Math.max(120, Math.round(width / 5));
      flowCanvas.height = Math.max(68, Math.round(height / 5));

      const gap = clamp(Math.round(Math.min(width, height) / 72), 4, 8);
      const radius = Math.max(0.55, gap * 0.105);
      const minSize = Math.min(width, height);
      const centerX = width / 2;
      const centerY = height / 2;
      const candidates: Dot[] = [];

      for (let y = gap / 2; y < height; y += gap) {
        for (let x = gap / 2; x < width; x += gap) {
          const dx = x - centerX;
          const dy = y - centerY;
          const angle = Math.atan2(dy, dx);
          candidates.push({
            x,
            y,
            nx: x / width,
            ny: y / height,
            radius,
            centerDistance: Math.hypot(dx, dy) / minSize,
            noise: hash(x, y),
            sin3: Math.sin(angle * 3),
            cos3: Math.cos(angle * 3),
            sin7: Math.sin(angle * 7),
            cos7: Math.cos(angle * 7),
          });
        }
      }

      dots = candidates;
    };

    const drawBackground = (now: number) => {
      const time = now * 0.0003;
      const flowWidth = flowCanvas.width;
      const flowHeight = flowCanvas.height;
      const diagonal = Math.hypot(flowWidth, flowHeight);
      flowContext.clearRect(0, 0, flowWidth, flowHeight);
      flowContext.fillStyle = '#000';
      flowContext.fillRect(0, 0, flowWidth, flowHeight);
      flowContext.save();
      flowContext.globalCompositeOperation = 'screen';

      for (let index = 0; index < 8; index += 1) {
        const column = index % 4;
        const row = Math.floor(index / 4);
        const phase = index * 1.73;
        const baseX = 0.13 + column * 0.247;
        const baseY = 0.27 + row * 0.46;
        const x = flowWidth * (baseX + Math.sin(time * (0.72 + index * 0.037) + phase) * 0.115);
        const y = flowHeight * (baseY + Math.cos(time * (0.61 + index * 0.031) + phase * 0.83) * 0.16);
        const stretchX = 0.82 + Math.sin(time * 0.55 + phase) * 0.24;
        const stretchY = 0.62 + Math.cos(time * 0.47 + phase * 1.2) * 0.2;
        const rotation = Math.sin(time * 0.31 + phase) * 0.9;
        const radius = diagonal * (0.36 + (index % 3) * 0.026);

        flowContext.save();
        flowContext.translate(x, y);
        flowContext.rotate(rotation);
        flowContext.scale(stretchX, stretchY);
        const gradient = flowContext.createRadialGradient(0, 0, 0, 0, 0, radius);
        const purple = index === 2 || index === 5;
        gradient.addColorStop(
          0,
          purple ? 'rgba(63, 55, 180, 0.115)' : index % 2 ? 'rgba(34, 94, 162, 0.15)' : 'rgba(25, 80, 150, 0.145)',
        );
        gradient.addColorStop(
          0.18,
          purple ? 'rgba(63, 55, 180, 0.115)' : index % 2 ? 'rgba(34, 94, 162, 0.15)' : 'rgba(25, 80, 150, 0.145)',
        );
        gradient.addColorStop(0.42, purple ? 'rgba(50, 42, 154, 0.1)' : 'rgba(28, 82, 153, 0.14)');
        gradient.addColorStop(0.52, purple ? 'rgba(38, 29, 126, 0.08)' : 'rgba(19, 70, 138, 0.12)');
        gradient.addColorStop(0.78, purple ? 'rgba(22, 15, 82, 0.045)' : 'rgba(7, 40, 98, 0.062)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        flowContext.fillStyle = gradient;
        flowContext.fillRect(-radius, -radius, radius * 2, radius * 2);
        flowContext.restore();
      }
      flowContext.restore();

      context.save();
      context.imageSmoothingEnabled = true;
      context.globalAlpha = 0.83;
      const bleed = Math.max(width, height) * 0.08;
      context.drawImage(flowCanvas, -bleed, -bleed, width + bleed * 2, height + bleed * 2);
      context.restore();
    };

    const drawFrame = (now: number) => {
      const elapsed = now - startTime;
      const cycle = (elapsed % CYCLE_DURATION) / CYCLE_DURATION;
      const travelled = elapsed * 0.00036;
      const source =
        travelled < LAUNCH_LENGTH
          ? sampleArc(LAUNCH_ARC, travelled)
          : sampleArc(PATROL_ARC, (travelled - LAUNCH_LENGTH) % PATROL_LENGTH);
      const launch = clamp(travelled / LAUNCH_LENGTH);
      const minSize = Math.min(width, height);
      const frame: FrameState = {
        scaleX: width / minSize,
        scaleY: height / minSize,
        waves: [],
      };

      for (let wave = 0; wave < 3; wave += 1) {
        const progress = (cycle + wave / 3) % 1;
        const offsetPhase = now * 0.00072 + (wave * Math.PI * 2) / 3;
        const offsetStrength = launch * (0.018 + 0.006 * Math.sin(now * 0.00043 + wave));
        frame.waves.push({
          sourceX: source.x + Math.cos(offsetPhase) * offsetStrength,
          sourceY: source.y + Math.sin(offsetPhase * 0.87) * offsetStrength,
          radius: progress * 0.76,
          birth: smoothRange(0, 0.08, progress),
          fade: 1 - smoothRange(0.48, 0.94, progress),
          sin3: Math.sin(cycle * Math.PI * 2 + wave * 2.1),
          cos3: Math.cos(cycle * Math.PI * 2 + wave * 2.1),
          sin7: Math.sin(cycle * 3.8 - wave * 1.2),
          cos7: Math.cos(cycle * 3.8 - wave * 1.2),
        });
      }

      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);
      context.fillStyle = '#000';
      context.fillRect(0, 0, width, height);
      drawBackground(now);

      for (const dot of dots) {
        const alpha = motionQuery.matches
          ? 1 - smoothRange(0.04, 0.15, Math.abs(dot.centerDistance - 0.24))
          : getParticleAlpha(dot, frame);
        if (alpha < 0.015) continue;
        const pulse = 0.96 + Math.sin(now * 0.0018 + dot.noise * 5) * 0.04;
        const driftX = Math.sin(now * 0.0015 + dot.ny * 5) * 2.5;
        const driftY = Math.cos(now * 0.0013 + dot.nx * 5) * 2.5;
        context.globalAlpha = Math.min(1, alpha * 1.28);
        context.fillStyle = '#d8f1ff';
        context.beginPath();
        context.arc(dot.x + driftX, dot.y + driftY, dot.radius * pulse, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
    };

    // 停止循环：清标志并取消挂起帧，可安全重复调用
    const stop = () => {
      running = false;
      cancelAnimationFrame(animationFrame);
    };

    const animate = (timestamp: number) => {
      // 循环期间若不再可见/后台/已停止，直接收尾，不再调度
      if (!running || !isVisible || document.hidden) {
        running = false;
        return;
      }
      // reduced-motion：只画一帧静态，不进入持续循环
      if (motionQuery.matches) {
        drawFrame(startTime);
        running = false;
        return;
      }
      animationFrame = requestAnimationFrame(animate);
      if (timestamp - lastFrameAt < FRAME_INTERVAL) return;
      drawFrame(timestamp);
      lastFrameAt = timestamp;
    };

    // 启动循环：幂等——已在跑则不重复调度，避免多个 rAF 循环互相竞争
    const start = () => {
      if (running || !isVisible || document.hidden) return;
      running = true;
      lastFrameAt = 0;
      animationFrame = requestAnimationFrame(animate);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      startTime = performance.now();
      start();
    };
    const resizeObserver = new ResizeObserver(() => {
      rebuild();
      start();
    });
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        isVisible = Boolean(entry?.isIntersecting);
        if (isVisible) {
          startTime = performance.now();
          start();
        } else {
          stop();
        }
      },
      { threshold: 0.05 },
    );

    resizeObserver.observe(canvas);
    intersectionObserver.observe(canvas);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    motionQuery.addEventListener('change', start);
    rebuild();
    start();

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      motionQuery.removeEventListener('change', start);
    };
  }, []);

  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 size-full" />;
};
